import {
  Button,
  Drawer,
  Empty,
  Input,
  Message,
  Modal,
  Spin,
  Typography,
} from "@arco-design/web-react";
import { History, Search } from "lucide-react";
import { useEffect, useState } from "react";

import {
  replaceCourseRouteFromHistory,
  searchCourseRouteHistory,
  type CourseRouteHistoryItem,
  type CourseRouteWorkspaceEditor,
} from "../repo/courseRouteFulfillmentRepo";
import { getSafeFulfillmentOrderActionErrorMessage } from "../service";

type FulfillmentOrderHistoryDrawerProps = {
  visible: boolean;
  workspaceId: string;
  itineraryId: string;
  revision: number;
  onClose: () => void;
  onReplaced: (editor: CourseRouteWorkspaceEditor) => void;
};

/** 统一展示确认件金额。 */
function formatAmount(value: string): string {
  return `¥${Number(value).toLocaleString("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** 将历史线路的起止日期整理成可读区间。 */
function formatDateRange(item: CourseRouteHistoryItem): string {
  if (!item.serviceDateFrom && !item.serviceDateTo) return "出行日期待补齐";
  if (item.serviceDateFrom === item.serviceDateTo || !item.serviceDateTo) {
    return item.serviceDateFrom ?? item.serviceDateTo ?? "出行日期待补齐";
  }
  return `${item.serviceDateFrom ?? "待补齐"} 至 ${item.serviceDateTo}`;
}

/** 从既有课程线路复制整份确认件明细。 */
export function FulfillmentOrderHistoryDrawer({
  visible,
  workspaceId,
  itineraryId,
  revision,
  onClose,
  onReplaced,
}: FulfillmentOrderHistoryDrawerProps) {
  const [keyword, setKeyword] = useState("");
  const [items, setItems] = useState<CourseRouteHistoryItem[]>([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [selected, setSelected] = useState<CourseRouteHistoryItem | null>(null);

  useEffect(() => {
    if (visible) return;
    setKeyword("");
    setItems([]);
    setSearched(false);
    setSelected(null);
  }, [visible]);

  const search = async () => {
    const title = keyword.trim();
    if (!title) {
      Message.warning("请输入课程线路标题");
      return;
    }
    setLoading(true);
    try {
      setItems(await searchCourseRouteHistory(title, itineraryId));
      setSearched(true);
    } catch (error) {
      Message.error(getSafeFulfillmentOrderActionErrorMessage(
        error,
        "历史确认件查询失败，请稍后重试",
      ));
    } finally {
      setLoading(false);
    }
  };

  const replace = async () => {
    if (!selected) return;
    setCopying(true);
    try {
      const editor = await replaceCourseRouteFromHistory(
        workspaceId,
        selected.workspaceId,
        revision,
      );
      onReplaced(editor);
      setSelected(null);
      onClose();
      Message.success("历史确认件已复制，请逐行确认日期、供应商和金额");
    } catch (error) {
      Message.error(getSafeFulfillmentOrderActionErrorMessage(
        error,
        "复制历史确认件失败，请稍后重试",
      ));
    } finally {
      setCopying(false);
    }
  };

  return (
    <>
      <Drawer
        title="获取历史数据"
        width={520}
        visible={visible}
        footer={null}
        unmountOnExit
        onCancel={onClose}
      >
        <section className="fulfillment-order-history-search" aria-label="查询历史确认件">
          <Typography.Text type="secondary">
            输入课程线路标题，选择一份已录入的确认件复制到当前线路。
          </Typography.Text>
          <div className="fulfillment-order-history-search-row">
            <Input
              aria-label="课程线路标题"
              value={keyword}
              disabled={loading}
              placeholder="例如：云南师大附中昆明研学课程"
              allowClear
              onChange={setKeyword}
              onPressEnter={() => void search()}
            />
            <Button
              type="primary"
              aria-label="查询历史确认件"
              loading={loading}
              disabled={!keyword.trim() || loading}
              onClick={() => void search()}
            >
              <Search aria-hidden />
              查询
            </Button>
          </div>
        </section>

        <div className="fulfillment-order-history-results" aria-live="polite">
          {loading ? (
            <div className="fulfillment-order-history-loading"><Spin dot /> 正在查询</div>
          ) : null}
          {!loading && searched && items.length === 0 ? (
            <Empty description="没有找到可复制的历史确认件" />
          ) : null}
          {!loading && items.map((item) => (
            <article className="fulfillment-order-history-item" key={item.workspaceId}>
              <div className="fulfillment-order-history-item-main">
                <span className="fulfillment-order-history-icon" aria-hidden><History /></span>
                <div>
                  <Typography.Title heading={6}>{item.itineraryTitle}</Typography.Title>
                  <Typography.Text type="secondary">{formatDateRange(item)}</Typography.Text>
                </div>
              </div>
              <div className="fulfillment-order-history-item-meta">
                <span>{item.lineCount} 条明细</span>
                <strong>{formatAmount(item.totalAmount)}</strong>
                <Button type="outline" onClick={() => setSelected(item)}>复制此确认件</Button>
              </div>
            </article>
          ))}
        </div>
      </Drawer>

      <Modal
        title="复制后将清空当前全部明细"
        visible={Boolean(selected)}
        okText="确认覆盖并复制"
        cancelText="取消"
        okButtonProps={{ loading: copying, status: "danger" }}
        cancelButtonProps={{ disabled: copying }}
        closable={!copying}
        maskClosable={!copying}
        onCancel={() => setSelected(null)}
        onOk={() => void replace()}
      >
        <p>
          历史明细会全部复制，并标记为待人工确认。请在生成供应商协议前逐行核对日期、
          服务供应商、结算供应商、数量和金额。
        </p>
      </Modal>
    </>
  );
}
